import * as React from 'react';
import styled from 'styled-components/macro';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { FoldersV2 } from 'lib/api/folders/getFoldersV2';
import { useCreateFolderMutation } from 'lib/api/folders/useCreateFolderMutation';
import { useUpdateFolderMutation } from 'lib/api/folders/useUpdateFolderMutation';
import { NewModal } from 'lib/components';
import { Button, Typography, Tooltip } from 'react-covideo-common';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import {
  FolderExistsResponse,
  checkFolderExist,
} from 'lib/api/folders/checkFolderName';
import { FormikSwitchField } from 'lib/components/formik/FormikSwitchField';
import { MdInfo } from 'react-icons/md';
import { IconWrapper } from 'lib/components/slider-control/styles';
import { Flex } from 'lib/components/styles/layout';
import { useAIFeatureAvailability } from 'lib/hooks/features/ai/useAIFeatureAvailability';
import { useTranslation } from 'react-i18next';

const ContentFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

type Props = {
  handleModalClose: (folder?: FoldersV2) => void;
  currentFolder?: Partial<FoldersV2>;
};

const checkFolderName = async (name: string): Promise<FolderExistsResponse> => {
  const folderName = name?.trim();
  if (!folderName) return { folderExist: false };
  return await checkFolderExist({ folderName });
};

const FolderSchema = (
  currentFolder: Partial<FoldersV2>,
  t: (key: string) => string
) =>
  Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t('Folder name is required'))
      .max(45, t('Folder name must be 45 characters or less'))
      .test('unique-name', t('Folder already exists!'), async function (value) {
        if (!value || value.trim() === currentFolder.name?.trim()) return true;
        const exists = await checkFolderName(value);
        return !exists.folderExist;
      }),
  });

const getIsAIFolderSwitchLabel = (t: (key: string) => string) => (
  <Flex gap='7px' flexDirection='row'>
    {t('Allow AI to gather content from this folder')}
    <Tooltip
      extendStyles={{
        popup: {
          padding: '10px',
          maxWidth: '300px',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          overflow: 'hidden',
        },
      }}
      popup={
        <Typography variant='small'>
          {t('AI will gather video recommendations from this folder.')}
        </Typography>
      }
    >
      <IconWrapper>
        <MdInfo size={20} />
      </IconWrapper>
    </Tooltip>
  </Flex>
);

export const CEModalFolders = ({ handleModalClose, currentFolder }: Props) => {
  const { t } = useTranslation();
  const { mutateAsync: createFolder } = useCreateFolderMutation();
  const { mutateAsync: updateFolder } = useUpdateFolderMutation();
  const { isVinReelsAvailable } = useAIFeatureAvailability();

  const isEdit = !!currentFolder?.name?.length;

  const handleFolderCreate = async (folder: Partial<FoldersV2>) => {
    const response = await createFolder(folder);
    handleModalClose(response);
  };
  const handleFolderEdit = async (folder: Partial<FoldersV2>) => {
    const response = await updateFolder(folder);
    handleModalClose(response);
  };
  const onSubmit = async (values: Partial<FoldersV2>) => {
    const isAIFolder = isVinReelsAvailable
      ? { isAIFolder: values.isAIFolder }
      : {};

    if (isEdit) {
      return handleFolderEdit({
        name: values.name?.trim(),
        folderId: currentFolder?.folderId,
        ...isAIFolder,
      });
    }

    return handleFolderCreate({
      name: values.name?.trim(),
      ...isAIFolder,
    });
  };

  return (
    <NewModal
      headerText={isEdit ? t('Edit Folder') : t('New Folder')}
      headerTextStyle={{ fontSize: '20px' }}
      closeModalOnBackdropOrEscPress={false}
      closeModal={() => handleModalClose()}
      shouldReturnFocusAfterClose={false}
    >
      <Formik
        initialValues={{
          name: currentFolder?.name || '',
          isAIFolder: currentFolder?.isAIFolder || false,
        }}
        validationSchema={FolderSchema(currentFolder || {}, t)}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, dirty, isValid, setFieldValue, values }) => {
          return (
            <Form>
              <FormikInputField
                name='name'
                label={t('Folder Name')}
                placeholder={t('Example folder')}
                isRequired={true}
                type={'text'}
                autoFocus={true}
                maxLength={45}
              />
              {isVinReelsAvailable && (
                <FormikSwitchField
                  extendStyles={{
                    wrapper: {
                      flexDirection: 'row-reverse',
                      justifyContent: 'flex-end',
                      margin: '25px 0 0 0',
                    },
                  }}
                  name='isAIFolder'
                  label={getIsAIFolderSwitchLabel(t)}
                  onChange={() =>
                    setFieldValue('isAIFolder', !values.isAIFolder)
                  }
                />
              )}

              <ContentFooter>
                <Button
                  type='submit'
                  text={isEdit ? t('Save Changes') : t('Create folder')}
                  disabled={isSubmitting || !dirty || !isValid}
                />
              </ContentFooter>
            </Form>
          );
        }}
      </Formik>
    </NewModal>
  );
};
